import { lazy, Suspense, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';

import { StageLoading } from '@/components/loading/default';
import { JuryStageLoading } from '@/components/loading/jury-stage';
import { RouteParams } from '@/routes';
import { useCandidates } from '@/state/use-candidates';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { STAGES } from '@repo/ui/state';

const ApplicantsStage = lazy(async () => ({
  default: (await import('./stages/applicants')).ApplicantsStage,
}));
const JuryStage = lazy(async () => ({
  default: (await import('./stages/jury')).JuryStage,
}));
const FinalistsStage = lazy(async () => ({
  default: (await import('./stages/finalists')).FinalistsStage,
}));
const DismissedStage = lazy(async () => ({
  default: (await import('./stages/dismissed')).DismissedStage,
}));

export const Stages = () => {
  const { goToStage, loading } = useCandidates();
  const { stageId, openCallId } = useParams<RouteParams>();

  useEffect(() => {
    goToStage(stageId!, openCallId!);
  }, [stageId]);

  const getPageByStage = useCallback(
    (stageId?: string) => {
      switch (stageId) {
        case STAGES.applicants.id:
          return <ApplicantsStage />;
        case STAGES.jury.id:
          return <JuryStage />;
        case STAGES.finalists.id:
          return <FinalistsStage />;
        case STAGES.dismissed.id:
          return <DismissedStage />;
        default:
          return <ApplicantsStage />;
      }
    },
    [stageId],
  );

  const getLoadingByStage = useCallback(
    (stageId: string) => {
      switch (stageId) {
        case '1':
          return <StageLoading />;
        case '2':
          return <JuryStageLoading />;
        case '3':
          return <StageLoading />;
        case '4':
          return <StageLoading />;
        default:
          return <StageLoading />;
      }
    },
    [stageId],
  );

  if (loading) {
    return getLoadingByStage(stageId!);
  }

  return (
    <Suspense fallback={<Skeleton className="size-full" />}>
      {getPageByStage(stageId)}
    </Suspense>
  );
};
